import { EMPTY_PATIENT } from "../constants/registrationConstants";
import { normalizeBase64Image, normalizeSearchGender } from "./patientSearch";
import { SOURCE_TYPE_VALUE_KEYS } from "./sectionConfiguration";

function parseApiDateOfBirth(data = {}) {
  const displayDob = data.date_of_birth;
  if (displayDob && /^\d{1,2}-\d{1,2}-\d{4}$/.test(String(displayDob))) {
    const [day, month, year] = String(displayDob).split("-");
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  if (displayDob && /^\d{4}-\d{2}-\d{2}$/.test(String(displayDob))) {
    return displayDob;
  }

  const year = data.year_of_birth;
  const month = data.month_of_birth;
  const day = data.day_of_birth;
  if (year && month && day) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return "";
}

function mapPersonalDetail(personal = {}) {
  return {
    title: personal.title || "",
    ageOnly: personal.age_only != null ? String(personal.age_only) : "",
    maritalStatus: personal.marital_status ? String(personal.marital_status).toUpperCase() : "",
    occupation: personal.occupation || "",
    nationality: personal.nationality || "Indian",
    religion: personal.religion || "",
    identityMarks: personal.identity_marks || "",
    familyPhysicianName: personal.family_physician_name || "",
    familyPhysicianPhone: personal.family_physician_phone || "",
    primaryMobile: personal.primary_mobile || "",
    secondaryMobile: personal.secondary_mobile || "",
  };
}

function mapFinanceEncounter(finance = {}) {
  const payer = finance.payer === "self_pay" ? "" : (finance.payer || "");
  return {
    admissionType: finance.admission_type || "GENERAL",
    financialClass: finance.financial_class || "CASH",
    payer,
    employeeId: finance.employee_id || "",
    insurancePolicyNo: finance.insurance_policy_no || "",
    policyExpiry: finance.policy_expiry || "",
  };
}

function mapSource(source) {
  const base = { ...EMPTY_PATIENT.source };
  if (!source || typeof source !== "object") return base;

  const sourceType = source.source_type || "";
  const sourceValue = source.source_type_value || "";

  if (!sourceType || sourceType === "WALK_IN") {
    return base;
  }

  base.sourceType = sourceType;
  const valueKey = SOURCE_TYPE_VALUE_KEYS[sourceType];
  if (valueKey) base[valueKey] = sourceValue;
  return base;
}

function mapDocuments(documents) {
  if (!Array.isArray(documents)) return [];
  return documents.map((doc) => ({
    type: doc.type || "",
    number: doc.number || "",
    issuedBy: doc.issued_by || "",
    expiry: doc.expiry || "",
    fileName: doc.file_name || "",
    fileBase64: "",
  }));
}

function mapContacts(contacts, fallbackPhone = "", fallbackEmail = "") {
  const list = Array.isArray(contacts)
    ? contacts.map((contact) => ({
      channel: contact.channel || "PHONE",
      type: contact.type || "PRIMARY",
      value: contact.value || "",
    }))
    : [];

  if (!list.length) {
    if (fallbackPhone) {
      list.push({ channel: "PHONE", type: "PRIMARY", value: fallbackPhone });
    }
    if (fallbackEmail) {
      list.push({ channel: "EMAIL", type: "PRIMARY", value: fallbackEmail });
    }
  }

  return list.length ? list : EMPTY_PATIENT.contacts;
}

function mapAddresses(addresses, apiData = {}) {
  const list = Array.isArray(addresses)
    ? addresses.map((address) => ({
      type: address.type || "PERMANENT",
      line1: address.line1 || "",
      city: address.city || "",
      district: address.district || "",
      state: address.state || "",
      country: address.country || "India",
      pincode: address.pincode || "",
    }))
    : [];

  if (!list.length) {
    const line1 = apiData.address_line || apiData.address || apiData.locality || "";
    const city = apiData.address_city || apiData.city || "";
    const district = apiData.address_district || apiData.district || "";
    const state = apiData.address_state || apiData.state || "";
    const pincode = apiData.address_pincode || apiData.pincode || "";
    if (line1 || city || district || state || pincode) {
      list.push({
        type: "PERMANENT",
        line1,
        city,
        district: String(district).toLowerCase(),
        state: String(state).toLowerCase(),
        country: "India",
        pincode,
      });
    }
  }

  return list.length ? list : EMPTY_PATIENT.addresses;
}

function mapFamily(members) {
  if (!Array.isArray(members)) return [];
  return members.map((member) => ({
    name: member.name || "",
    relation: member.relation || "",
    phone: member.phone || "",
    nextOfKin: Boolean(member.next_of_kin),
  }));
}

function mapGuarantors(guarantors) {
  if (!Array.isArray(guarantors)) return [];
  return guarantors.map((guarantor) => ({
    name: guarantor.name || "",
    relation: guarantor.relation || "",
    phone: guarantor.phone || "",
    idType: guarantor.id_type || "",
    idValue: guarantor.id_value || "",
  }));
}

function mapEmergencyContacts(contacts) {
  const list = Array.isArray(contacts)
    ? contacts.map((contact) => ({
      name: contact.name || "",
      relation: contact.relation || "",
      phone: contact.phone || "",
      address: contact.address || "",
    }))
    : [];
  return list.length ? list : EMPTY_PATIENT.emergencyContacts;
}

function mapAttendingDoctors(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => ({
    rowId: `att-${index}-${row.doctor_id || Date.now()}`,
    department: row.department_id || "",
    doctor_id: row.doctor_id || "",
    notes: row.notes || "",
  }));
}

/** Maps get_patient_admission_data/ response `data` object into form patient state. */
export function mapAdmissionDataToPatient(apiData = {}, meta = {}) {
  const personal = apiData.patient_personal_detail || {};
  const patientId = meta.patient_id || apiData.patient_id || "";
  const uhid = meta.uhid || apiData.uhid || "";
  const phoneNumber =
    apiData.phone_number
    || apiData.phone_no
    || personal.primary_mobile
    || "";
  const email = apiData.email || "";

  return {
    uhid: uhid || patientId,
    patient_id: patientId,
    admission_id: meta.admission_id ?? null,
    name: apiData.name || apiData.patient_name || "",
    gender: normalizeSearchGender(apiData.gender),
    dob: parseApiDateOfBirth(apiData) || apiData.dob || "",
    abhaAddress: apiData.abha_id != null ? (apiData.abha_id || "") : "",
    abhaNumber: apiData.abha_no != null && apiData.abha_no !== "NA" ? (apiData.abha_no || "") : "",
    bloodGroup: apiData.blood_group || "",
    photoUrl: normalizeBase64Image(apiData.uploaded_photo || apiData.photo),
    primaryMobile: phoneNumber,
    ...mapPersonalDetail({ ...personal, primary_mobile: phoneNumber || personal.primary_mobile }),
    contacts: mapContacts(apiData.contacts, phoneNumber, email),
    addresses: mapAddresses(apiData.addresses, apiData),
    documents: mapDocuments(apiData.documents),
    family: mapFamily(apiData.family_members),
    guarantors: mapGuarantors(apiData.guarantors),
    emergencyContacts: mapEmergencyContacts(apiData.emergency_contacts),
    source: mapSource(apiData.patient_source),
    encounter: {
      ...EMPTY_PATIENT.encounter,
      ...mapFinanceEncounter(apiData.finance_details || {}),
      provisionalDiagnosis: apiData.provisional_diagnosis || "",
      tpaCode: apiData.tpa_details?.tpa_code || "",
      tpaName: apiData.tpa_details?.tpa_name || "",
      admittingDoctorId: apiData.assigned_doctor_id || "",
      admittingDoctorName: apiData.assigned_doctor_name || apiData.admitting_doctor_name || "",
      department: apiData.assigned_department || "",
      departmentId: apiData.assigned_department_id || "",
      bedId: apiData.bed_id || "",
      wardNo: apiData.bed_type || "",
      newBedStatus: apiData.new_bed_status || "reserved",
      attendingDoctorRows: mapAttendingDoctors(apiData.attending_doctors),
    },
  };
}
